/**
 * DocumentStatusBadge Component
 * Displays document processing status with appropriate colors and icons
 */
import { cn } from '@/utils/cn';
import type { DocumentStatus } from '@/types/document';

export interface DocumentStatusBadgeProps {
  status: DocumentStatus;
  className?: string;
}

/**
 * Spinner icon for processing state
 */
function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

interface StatusConfig {
  label: string;
  bgColor: string;
  textColor: string;
  dotColor: string;
  hasSpinner?: boolean;
  tooltip: string;
}

/**
 * Get configuration for status display
 */
function getStatusConfig(status: DocumentStatus): StatusConfig {
  switch (status) {
    // All processing states - actual backend statuses
    case 'uploading':
    case 'processing':
    case 'ingested':
    case 'classifying':
    case 'classified':
    case 'parsing':
      return {
        label: 'Processing',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        dotColor: 'bg-blue-500',
        hasSpinner: true,
        tooltip: 'Document is being processed',
      };

    // Awaiting user action - document processed, needs user to continue
    case 'pending_account':
    case 'pending_review':
    case 'staged':
    case 'parsed':
      return {
        label: 'Pending Review',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-700',
        dotColor: 'bg-yellow-500',
        tooltip: 'Document needs your attention to complete import',
      };

    // Successfully completed - transactions imported
    case 'completed':
    case 'committed':
      return {
        label: 'Completed',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        dotColor: 'bg-green-500',
        tooltip: 'Document has been successfully imported',
      };

    case 'failed':
    case 'error':
      return {
        label: 'Failed',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        dotColor: 'bg-red-500',
        tooltip: 'Document processing failed',
      };

    default:
      return {
        label: 'Unknown',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        dotColor: 'bg-gray-400',
        tooltip: 'Unknown status',
      };
  }
}

export function DocumentStatusBadge({ status, className }: DocumentStatusBadgeProps) {
  const config = getStatusConfig(status);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium',
        config.bgColor,
        config.textColor,
        className
      )}
      title={config.tooltip}
    >
      {config.hasSpinner ? (
        <SpinnerIcon className="w-3 h-3" />
      ) : (
        <span className={cn('w-1.5 h-1.5 rounded-full', config.dotColor)} aria-hidden="true" />
      )}
      <span>{config.label}</span>
    </span>
  );
}

export default DocumentStatusBadge;
